/**
 * Workflow Chat Component
 *
 * Natural language interface for creating and modifying workflows.
 * Users can type or speak commands like:
 * - "Create a KYC flow for crypto exchanges"
 * - "Add liveness check after selfie"
 * - "If risk score > 50, require manual review"
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Send,
  Mic,
  MicOff,
  Loader2,
  Sparkles,
  FileText,
  Upload,
  ChevronDown,
  ChevronUp,
  Check,
  AlertTriangle,
  X,
  Image,
  ArrowRightLeft,
} from 'lucide-react';
import {
  useChatWorkflow,
  useParsePolicy,
  useUploadPolicyFile,
  useExtractRequirements,
  useGenerateFromRequirements,
  useMigrateFromScreenshot,
} from '../../hooks/useWorkflowBuilder';

// ============================================
// STYLES
// ============================================

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: 'var(--bg-primary)',
    borderLeft: '1px solid var(--border-color)',
  },
  header: {
    padding: '16px',
    borderBottom: '1px solid var(--border-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'var(--bg-secondary)',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 600,
    fontSize: '14px',
    color: 'var(--text-primary)',
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  message: {
    maxWidth: '85%',
    padding: '12px 16px',
    borderRadius: '12px',
    fontSize: '14px',
    lineHeight: '1.5',
  },
  userMessage: {
    alignSelf: 'flex-end',
    background: 'var(--accent-primary)',
    color: 'white',
    borderBottomRightRadius: '4px',
  },
  assistantMessage: {
    alignSelf: 'flex-start',
    background: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    borderBottomLeftRadius: '4px',
  },
  inputContainer: {
    padding: '16px',
    borderTop: '1px solid var(--border-color)',
    background: 'var(--bg-secondary)',
  },
  inputLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: '8px',
    display: 'block',
  },
  inputWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'var(--bg-primary)',
    border: '2px solid var(--accent-primary)',
    borderRadius: '12px',
    padding: '12px 16px',
    boxShadow: '0 2px 8px var(--accent-glow)',
  },
  input: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    fontSize: '15px',
    color: 'var(--text-primary)',
    outline: 'none',
  },
  sendButton: {
    padding: '8px',
    borderRadius: '6px',
    border: 'none',
    background: 'var(--accent-primary)',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.2s',
  },
  micButton: {
    padding: '8px',
    borderRadius: '6px',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  micButtonActive: {
    background: '#22c55e',
    color: 'white',
    animation: 'pulse-recording 1.5s infinite',
  },
  suggestions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '12px',
  },
  suggestionsLabel: {
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  suggestionChip: {
    padding: '8px 14px',
    borderRadius: '20px',
    border: '1px solid var(--accent-primary)',
    background: 'var(--accent-glow-medium)',
    color: 'var(--accent-primary)',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  workflowPreview: {
    marginTop: '12px',
    padding: '12px',
    background: 'var(--bg-primary)',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  previewTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
  },
  policyUpload: {
    padding: '16px',
    border: '2px dashed var(--border-color)',
    borderRadius: '8px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  confidenceBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
  },
};

// ============================================
// CHAT COMPONENT
// ============================================

export function WorkflowChat({
  workflowId,
  onWorkflowCreated,
  onConfigUpdate,
  onClose,
  // Legacy props for embedded mode
  onWorkflowUpdate,
  onApplyChanges,
  collapsed,
  onToggle,
  // New props
  initialPanel = null, // 'migration' to open with migration panel visible
}) {
  // Use new props or fall back to legacy
  const handleApplyChanges = onConfigUpdate || onApplyChanges;
  const handleClose = onClose || onToggle;
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "👋 Hi! I'm your AI workflow assistant.\n\nType what you want to build in the box below, and I'll create a workflow for you. For example:\n\n• \"Create a KYC flow with ID verification and selfie\"\n• \"Add liveness check after document upload\"\n• \"Route high-risk applicants to manual review\"\n\nOr click a Quick Action to get started!",
    },
  ]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [showPolicyUpload, setShowPolicyUpload] = useState(false);
  const [showMigrationPanel, setShowMigrationPanel] = useState(initialPanel === 'migration');
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  const chatMutation = useChatWorkflow();
  const policyMutation = useParsePolicy();
  const uploadPolicyMutation = useUploadPolicyFile();
  const migrationMutation = useMigrateFromScreenshot();

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle sending a message
  const handleSend = useCallback(async () => {
    if (!input.trim() || chatMutation.isPending) return;

    const userMessage = input.trim();
    setInput('');

    // Add user message
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);

    try {
      const response = await chatMutation.mutateAsync({
        message: userMessage,
        conversationId,
        workflowId,
        context: { industry: 'crypto', region: 'global' },
      });

      // Update conversation ID
      if (response.conversation_id) {
        setConversationId(response.conversation_id);
      }

      // Add assistant message
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: response.message,
          workflowSpec: response.workflow_spec,
          confidence: response.confidence,
          suggestedActions: response.suggested_actions,
          // Clarification support
          needsClarification: response.needs_clarification,
          clarificationOptions: response.clarification_options,
        },
      ]);

      // Note: Don't auto-apply workflow_spec here.
      // User must click "Apply to Canvas" button to apply changes.
      // This prevents accidental overwrites and gives user control.
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `Sorry, I encountered an error: ${error.message}. Please try again.`,
          isError: true,
        },
      ]);
    }
  }, [input, chatMutation, conversationId, workflowId]);

  // Handle voice input
  const toggleVoice = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Voice input is not supported in this browser.');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'en-US';

    recognitionRef.current.onstart = () => setIsListening(true);
    recognitionRef.current.onend = () => setIsListening(false);

    recognitionRef.current.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join('');
      setInput(transcript);

      if (event.results[0].isFinal) {
        setIsListening(false);
      }
    };

    recognitionRef.current.onerror = () => {
      setIsListening(false);
    };

    recognitionRef.current.start();
  }, [isListening]);

  // Handle policy upload (supports both legacy and 2-step flow)
  const handlePolicyUpload = useCallback(async (text, file, generatedResult) => {
    setShowPolicyUpload(false);

    const displayText = text
      ? text.substring(0, 200)
      : file?.name || 'document';

    setMessages((prev) => [
      ...prev,
      { role: 'user', content: `[Uploaded policy document: ${file?.name || 'pasted text'}]\n\n${displayText}...` },
    ]);

    // If we already have a generated result from the 2-step flow, use it directly
    if (generatedResult) {
      const clarifications = Array.isArray(generatedResult?.clarifications_needed)
        ? generatedResult.clarifications_needed.map((q) => typeof q === 'string' ? q : String(q?.message || q?.value || q || ''))
        : [];
      const confidenceScore = typeof generatedResult?.confidence_score === 'number'
        ? generatedResult.confidence_score
        : 0;

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `I've analyzed your policy and generated a branching workflow. Here's what I found:\n\n${clarifications.length ? `**Questions:**\n${clarifications.map((q) => `• ${q}`).join('\n')}` : 'The workflow is ready to review.'}`,
          workflowSpec: generatedResult?.config || {},
          confidence: confidenceScore,
          coverage: generatedResult?.compliance_coverage || {},
        },
      ]);
      return;
    }

    // Legacy flow: direct parse (for text files or fallback)
    try {
      let response;
      if (file && !text) {
        response = await uploadPolicyMutation.mutateAsync({
          file,
          industry: 'crypto',
          region: 'global',
        });
      } else {
        response = await policyMutation.mutateAsync({
          policyText: text,
          industry: 'crypto',
          region: 'global',
        });
      }

      const clarifications = Array.isArray(response?.clarifications_needed)
        ? response.clarifications_needed.map((q) => typeof q === 'string' ? q : String(q?.message || q?.value || q || ''))
        : [];
      const confidenceScore = typeof response?.confidence_score === 'number'
        ? response.confidence_score
        : 0;

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `I've analyzed your policy and created a workflow. Here's what I found:\n\n${clarifications.length ? `**Questions:**\n${clarifications.map((q) => `• ${q}`).join('\n')}` : 'The workflow is ready to review.'}`,
          workflowSpec: response?.config || {},
          confidence: confidenceScore,
          coverage: response?.compliance_coverage || {},
        },
      ]);
    } catch (error) {
      const errorMsg = typeof error === 'string'
        ? error
        : typeof error?.message === 'string'
          ? error.message
          : 'An unexpected error occurred';
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `Failed to parse policy: ${errorMsg}`,
          isError: true,
        },
      ]);
    }
  }, [policyMutation, uploadPolicyMutation]);

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
  };

  if (collapsed) {
    return (
      <div
        style={{
          ...styles.container,
          width: '48px',
          alignItems: 'center',
          padding: '16px 0',
        }}
      >
        <button
          onClick={handleClose}
          style={{
            ...styles.micButton,
            transform: 'rotate(-90deg)',
          }}
          title="Open AI Chat"
        >
          <Sparkles size={20} />
        </button>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, width: '360px' }}>
      {/* Keyframe animations */}
      <style>{`
        @keyframes pulse-recording {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
      `}</style>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          <Sparkles size={16} style={{ color: 'var(--accent-primary)' }} />
          AI Workflow Builder
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => {
              setShowMigrationPanel(!showMigrationPanel);
              setShowPolicyUpload(false);
            }}
            style={{
              ...styles.micButton,
              ...(showMigrationPanel ? { background: 'var(--accent-glow-medium)', color: 'var(--accent-primary)' } : {}),
            }}
            title="Migrate workflow from screenshot"
          >
            <ArrowRightLeft size={16} />
          </button>
          <button
            onClick={() => {
              setShowPolicyUpload(!showPolicyUpload);
              setShowMigrationPanel(false);
            }}
            style={styles.micButton}
            title="Upload policy document"
          >
            <Upload size={16} />
          </button>
          <button onClick={handleClose} style={styles.micButton}>
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Workflow Migration Panel */}
      {showMigrationPanel && (
        <WorkflowMigrationPanel
          onMigrate={(result) => {
            setShowMigrationPanel(false);
            // Add migration result to messages
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content: `✨ Successfully analyzed your ${result.source_platform || 'competitor'} workflow screenshot!\n\n**Detected Steps:** ${result.detected_steps?.join(', ') || 'Various steps'}\n\n${result.migration_notes?.length ? `**Notes:** ${result.migration_notes.join(', ')}` : ''}`,
                workflowSpec: result.config,
                confidence: result.confidence_score,
              },
            ]);
          }}
          onClose={() => setShowMigrationPanel(false)}
          isLoading={migrationMutation.isPending}
          mutation={migrationMutation}
        />
      )}

      {/* Policy Upload Panel */}
      {showPolicyUpload && (
        <PolicyUploadPanel
          onUpload={handlePolicyUpload}
          onClose={() => setShowPolicyUpload(false)}
          isLoading={policyMutation.isPending || uploadPolicyMutation.isPending}
        />
      )}

      {/* Messages */}
      <div style={styles.messages}>
        {messages.map((msg, idx) => (
          <MessageBubble
            key={idx}
            message={msg}
            onApply={msg.workflowSpec && handleApplyChanges ? () => {
              console.log('[WorkflowChat] Applying workflow spec:', msg.workflowSpec);
              handleApplyChanges(msg.workflowSpec);
            } : null}
            onSuggestionClick={handleSuggestionClick}
          />
        ))}
        {chatMutation.isPending && (
          <div style={{ ...styles.message, ...styles.assistantMessage }}>
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={styles.inputContainer}>
        <label style={styles.inputLabel}>
          ✨ What would you like to build?
        </label>
        <div style={styles.inputWrapper}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="e.g., Create a KYC flow with ID verification and selfie..."
            style={styles.input}
            disabled={chatMutation.isPending}
            autoFocus
            data-testid="chat-input"
          />
          <button
            onClick={toggleVoice}
            style={{
              ...styles.micButton,
              ...(isListening ? styles.micButtonActive : {}),
            }}
            title={isListening ? 'Stop listening' : 'Voice input'}
          >
            {isListening ? <MicOff size={16} /> : <Mic size={16} />}
          </button>
          <button
            onClick={handleSend}
            disabled={!input.trim() || chatMutation.isPending}
            style={{
              ...styles.sendButton,
              opacity: !input.trim() || chatMutation.isPending ? 0.5 : 1,
            }}
            title="Send message"
            data-testid="chat-send-btn"
          >
            {chatMutation.isPending ? (
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Send size={16} />
            )}
          </button>
        </div>

        {/* Quick suggestions */}
        <div style={{ marginTop: '12px' }}>
          <div style={styles.suggestionsLabel}>Quick Actions</div>
          <div style={styles.suggestions}>
            {[
              'Create KYC flow',
              'Add liveness check',
              'Add sanctions screening',
            ].map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => handleSuggestionClick(suggestion)}
                style={styles.suggestionChip}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// MESSAGE BUBBLE COMPONENT
// ============================================

function MessageBubble({ message, onApply, onSuggestionClick }) {
  const isUser = message.role === 'user';
  const [showPreview, setShowPreview] = useState(true);

  return (
    <div
      style={{
        ...styles.message,
        ...(isUser ? styles.userMessage : styles.assistantMessage),
        ...(message.isError ? { borderLeft: '3px solid #ef4444' } : {}),
      }}
    >
      {/* Message content */}
      <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>

      {/* Confidence badge */}
      {message.confidence !== undefined && (
        <div
          style={{
            ...styles.confidenceBadge,
            marginTop: '8px',
            background: message.confidence > 0.7 ? '#10b98120' : '#f59e0b20',
            color: message.confidence > 0.7 ? '#10b981' : '#f59e0b',
          }}
        >
          {message.confidence > 0.7 ? <Check size={12} /> : <AlertTriangle size={12} />}
          {Math.round(message.confidence * 100)}% confidence
        </div>
      )}

      {/* Workflow preview */}
      {message.workflowSpec && (
        <div style={styles.workflowPreview}>
          <div style={styles.previewHeader}>
            <span style={styles.previewTitle}>Generated Workflow</span>
            <button
              onClick={() => setShowPreview(!showPreview)}
              style={{ ...styles.micButton, padding: '4px' }}
            >
              {showPreview ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>
          {showPreview && (
            <>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {message.workflowSpec.nodes?.length || 0} nodes,{' '}
                {message.workflowSpec.edges?.length || 0} connections
              </div>
              {onApply && (
                <button
                  onClick={onApply}
                  style={{
                    ...styles.sendButton,
                    width: '100%',
                    marginTop: '8px',
                    padding: '8px 16px',
                  }}
                >
                  <Check size={14} style={{ marginRight: '8px' }} />
                  Apply to Canvas
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Suggested actions */}
      {message.suggestedActions?.length > 0 && (
        <div style={{ ...styles.suggestions, marginTop: '8px' }}>
          {message.suggestedActions.map((action, idx) => (
            <button
              key={idx}
              onClick={() => onSuggestionClick?.(action)}
              style={styles.suggestionChip}
            >
              {action}
            </button>
          ))}
        </div>
      )}

      {/* Clarification options */}
      {message.needsClarification && message.clarificationOptions?.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <div style={{ ...styles.suggestionsLabel, marginBottom: '8px' }}>
            Choose an option:
          </div>
          <div style={{ ...styles.suggestions, flexDirection: 'column', alignItems: 'stretch' }}>
            {message.clarificationOptions.map((opt, idx) => (
              <button
                key={idx}
                onClick={() => onSuggestionClick?.(opt.value)}
                style={{
                  ...styles.suggestionChip,
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  borderRadius: '8px',
                  marginBottom: '8px',
                }}
              >
                <span style={{ fontWeight: 600 }}>{opt.label}</span>
                {opt.description && (
                  <span style={{ fontSize: '12px', opacity: 0.8, fontWeight: 400 }}>
                    {opt.description}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// POLICY UPLOAD PANEL (with 2-step flow)
// ============================================

function PolicyUploadPanel({ onUpload, onClose, isLoading }) {
  const [policyText, setPolicyText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isBinaryFile, setIsBinaryFile] = useState(false);
  const [requirements, setRequirements] = useState(null);
  const fileInputRef = useRef(null);

  const extractMutation = useExtractRequirements();
  const generateMutation = useGenerateFromRequirements();

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (['txt', 'md'].includes(ext)) {
      const text = await file.text();
      setPolicyText(text);
      setSelectedFile(file);
      setIsBinaryFile(false);
    } else {
      setSelectedFile(file);
      setIsBinaryFile(true);
      setPolicyText(`[${file.name}] - will be processed by server`);
    }
  };

  const handleExtractRequirements = async () => {
    if (!selectedFile && !policyText.trim()) return;

    if (isBinaryFile && selectedFile) {
      try {
        const result = await extractMutation.mutateAsync({
          file: selectedFile,
          industry: 'crypto',
          region: 'global',
        });
        setRequirements(result);
      } catch (error) {
        // Fall back to legacy flow on error
        onUpload(null, selectedFile);
      }
    } else if (policyText.trim()) {
      // For text files, use the legacy parse flow (no file to upload)
      onUpload(policyText, selectedFile);
    }
  };

  const handleGenerateWorkflow = async (confirmedRequirements) => {
    try {
      const result = await generateMutation.mutateAsync({
        requirements: confirmedRequirements,
        industry: 'crypto',
        region: 'global',
      });
      // Pass the workflow config to parent
      onUpload(null, selectedFile, result);
    } catch (error) {
      onUpload(null, selectedFile);
    }
  };

  const isExtracting = extractMutation.isPending;
  const isGenerating = generateMutation.isPending;

  // Show requirements review panel after extraction
  if (requirements) {
    return (
      <PolicyRequirementsPanel
        requirements={requirements}
        onGenerate={handleGenerateWorkflow}
        onBack={() => setRequirements(null)}
        onClose={onClose}
        isGenerating={isGenerating}
      />
    );
  }

  return (
    <div
      style={{
        padding: '16px',
        borderBottom: '1px solid var(--border-color)',
        background: 'var(--bg-tertiary)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
        <span style={{ fontWeight: 600, fontSize: '14px' }}>Upload Policy Document</span>
        <button onClick={onClose} style={styles.micButton}>
          <X size={16} />
        </button>
      </div>

      <div
        style={styles.policyUpload}
        onClick={() => fileInputRef.current?.click()}
      >
        <FileText size={24} style={{ color: 'var(--text-secondary)', marginBottom: '8px' }} />
        <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
          Click to upload or drag & drop
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          PDF, DOCX, or TXT
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.txt,.md"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
        />
      </div>

      {policyText && (
        <>
          <textarea
            value={policyText}
            onChange={(e) => setPolicyText(e.target.value)}
            placeholder="Or paste your policy text here..."
            style={{
              width: '100%',
              minHeight: '120px',
              marginTop: '12px',
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              fontSize: '13px',
              resize: 'vertical',
            }}
          />
          <button
            onClick={handleExtractRequirements}
            disabled={isLoading || isExtracting}
            style={{
              ...styles.sendButton,
              width: '100%',
              marginTop: '12px',
              padding: '10px',
            }}
          >
            {isExtracting ? (
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <>
                <Sparkles size={16} style={{ marginRight: '8px' }} />
                Analyze Policy
              </>
            )}
          </button>
        </>
      )}

      {!policyText && (
        <textarea
          value={policyText}
          onChange={(e) => setPolicyText(e.target.value)}
          placeholder="Or paste your policy text here..."
          style={{
            width: '100%',
            minHeight: '80px',
            marginTop: '12px',
            padding: '12px',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            background: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            fontSize: '13px',
            resize: 'vertical',
          }}
        />
      )}
    </div>
  );
}

// ============================================
// POLICY REQUIREMENTS PANEL (User Review)
// ============================================

function PolicyRequirementsPanel({ requirements, onGenerate, onBack, onClose, isGenerating }) {
  const [screeningLists, setScreeningLists] = useState(requirements.screening_lists || []);
  const [pepScreening, setPepScreening] = useState(requirements.pep_screening || false);
  const [adverseMedia, setAdverseMedia] = useState(requirements.adverse_media || false);
  const riskTiers = requirements.risk_tiers || [];
  const escalationChain = requirements.escalation_chain || [];
  const reportingObligations = requirements.reporting_obligations || [];
  const monitoring = requirements.monitoring || {};
  const externalApis = requirements.external_apis || [];

  const allScreeningOptions = ['OFAC', 'UN', 'EU', 'UK', 'AU', 'CA'];

  const toggleScreeningList = (list) => {
    setScreeningLists((prev) =>
      prev.includes(list) ? prev.filter((l) => l !== list) : [...prev, list]
    );
  };

  const handleGenerate = () => {
    onGenerate({
      screening_lists: screeningLists,
      pep_screening: pepScreening,
      adverse_media: adverseMedia,
      risk_tiers: riskTiers,
      escalation_chain: escalationChain,
      reporting_obligations: reportingObligations,
      monitoring,
      external_apis: externalApis,
      confidence: requirements.confidence || 0.5,
    });
  };

  const panelStyles = {
    section: {
      marginBottom: '16px',
      padding: '12px',
      background: 'var(--bg-primary)',
      borderRadius: '8px',
      border: '1px solid var(--border-color)',
    },
    sectionTitle: {
      fontSize: '13px',
      fontWeight: 600,
      color: 'var(--text-primary)',
      marginBottom: '8px',
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '4px 0',
      fontSize: '13px',
      color: 'var(--text-primary)',
      cursor: 'pointer',
    },
    toggle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '6px 0',
      fontSize: '13px',
      color: 'var(--text-primary)',
    },
    chip: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 500,
      margin: '2px',
    },
    tierCard: {
      padding: '8px 12px',
      borderRadius: '6px',
      border: '1px solid var(--border-color)',
      marginBottom: '6px',
      fontSize: '12px',
    },
  };

  return (
    <div
      style={{
        padding: '16px',
        borderBottom: '1px solid var(--border-color)',
        background: 'var(--bg-tertiary)',
        maxHeight: '500px',
        overflowY: 'auto',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
        <span style={{ fontWeight: 600, fontSize: '14px' }}>Review Requirements</span>
        <div style={{ display: 'flex', gap: '4px' }}>
          <button onClick={onBack} style={{ ...styles.micButton, fontSize: '12px' }} title="Back">
            <ChevronUp size={16} />
          </button>
          <button onClick={onClose} style={styles.micButton}>
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Confidence indicator */}
      {requirements.confidence > 0 && (
        <div style={{
          ...styles.confidenceBadge,
          marginBottom: '12px',
          background: requirements.confidence > 0.7 ? '#10b98120' : '#f59e0b20',
          color: requirements.confidence > 0.7 ? '#10b981' : '#f59e0b',
        }}>
          {requirements.confidence > 0.7 ? <Check size={12} /> : <AlertTriangle size={12} />}
          {' '}{Math.round(requirements.confidence * 100)}% extraction confidence
        </div>
      )}

      {/* Screening Lists */}
      <div style={panelStyles.section}>
        <div style={panelStyles.sectionTitle}>Sanctions Screening Lists</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {allScreeningOptions.map((list) => (
            <label key={list} style={{
              ...panelStyles.chip,
              background: screeningLists.includes(list) ? 'rgba(59, 130, 246, 0.15)' : 'var(--bg-secondary)',
              color: screeningLists.includes(list) ? '#3b82f6' : 'var(--text-secondary)',
              border: `1px solid ${screeningLists.includes(list) ? '#3b82f6' : 'var(--border-color)'}`,
              cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={screeningLists.includes(list)}
                onChange={() => toggleScreeningList(list)}
                style={{ display: 'none' }}
              />
              {screeningLists.includes(list) && <Check size={10} />}
              {list}
            </label>
          ))}
        </div>
      </div>

      {/* PEP & Adverse Media Toggles */}
      <div style={panelStyles.section}>
        <div style={panelStyles.toggle}>
          <span>PEP Screening</span>
          <button
            onClick={() => setPepScreening(!pepScreening)}
            style={{
              width: '36px',
              height: '20px',
              borderRadius: '10px',
              border: 'none',
              background: pepScreening ? '#3b82f6' : 'var(--border-color)',
              position: 'relative',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
          >
            <div style={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: 'white',
              position: 'absolute',
              top: '2px',
              left: pepScreening ? '18px' : '2px',
              transition: 'left 0.2s',
            }} />
          </button>
        </div>
        <div style={panelStyles.toggle}>
          <span>Adverse Media</span>
          <button
            onClick={() => setAdverseMedia(!adverseMedia)}
            style={{
              width: '36px',
              height: '20px',
              borderRadius: '10px',
              border: 'none',
              background: adverseMedia ? '#3b82f6' : 'var(--border-color)',
              position: 'relative',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
          >
            <div style={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: 'white',
              position: 'absolute',
              top: '2px',
              left: adverseMedia ? '18px' : '2px',
              transition: 'left 0.2s',
            }} />
          </button>
        </div>
      </div>

      {/* Risk Tiers */}
      {riskTiers.length > 0 && (
        <div style={panelStyles.section}>
          <div style={panelStyles.sectionTitle}>Risk Tiers</div>
          {riskTiers.map((tier, idx) => (
            <div key={idx} style={panelStyles.tierCard}>
              <div style={{ fontWeight: 600, marginBottom: '2px' }}>{tier.name || `Tier ${idx + 1}`}</div>
              <div style={{ color: 'var(--text-secondary)' }}>{tier.criteria || 'No criteria specified'}</div>
            </div>
          ))}
        </div>
      )}

      {/* Escalation Chain */}
      {escalationChain.length > 0 && (
        <div style={panelStyles.section}>
          <div style={panelStyles.sectionTitle}>Escalation Chain</div>
          {escalationChain.map((esc, idx) => (
            <div key={idx} style={{ fontSize: '12px', padding: '4px 0', color: 'var(--text-primary)' }}>
              <span style={{ fontWeight: 500 }}>{esc.role}</span>
              <span style={{ color: 'var(--text-secondary)' }}> - {esc.trigger_condition}</span>
            </div>
          ))}
        </div>
      )}

      {/* Monitoring */}
      {monitoring.conditions?.length > 0 && (
        <div style={panelStyles.section}>
          <div style={panelStyles.sectionTitle}>Continuous Monitoring</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            Frequency: {monitoring.frequency || 'Not specified'}
          </div>
          {monitoring.conditions.map((cond, idx) => (
            <div key={idx} style={{ fontSize: '12px', padding: '2px 0', color: 'var(--text-primary)' }}>
              {cond}
            </div>
          ))}
        </div>
      )}

      {/* External APIs */}
      {externalApis.length > 0 && (
        <div style={panelStyles.section}>
          <div style={panelStyles.sectionTitle}>External APIs <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 400 }}>(future)</span></div>
          {externalApis.map((api, idx) => (
            <div key={idx} style={{ fontSize: '12px', padding: '4px 0', color: 'var(--text-primary)' }}>
              <span style={{ fontWeight: 500 }}>{api.purpose}</span>
              {api.description && <span style={{ color: 'var(--text-secondary)' }}> - {api.description}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Reporting */}
      {reportingObligations.length > 0 && (
        <div style={panelStyles.section}>
          <div style={panelStyles.sectionTitle}>Reporting Obligations</div>
          {reportingObligations.map((rep, idx) => (
            <div key={idx} style={{ fontSize: '12px', padding: '4px 0', color: 'var(--text-primary)' }}>
              <span style={{ fontWeight: 500 }}>{rep.type}</span>
              <span style={{ color: 'var(--text-secondary)' }}> - {rep.trigger}</span>
            </div>
          ))}
        </div>
      )}

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={isGenerating}
        style={{
          ...styles.sendButton,
          width: '100%',
          padding: '12px',
          fontSize: '14px',
          fontWeight: 600,
          borderRadius: '8px',
        }}
      >
        {isGenerating ? (
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
        ) : (
          <>
            <Sparkles size={16} style={{ marginRight: '8px' }} />
            Generate Workflow
          </>
        )}
      </button>
    </div>
  );
}

// ============================================
// WORKFLOW MIGRATION PANEL (Screenshot-based)
// ============================================

function WorkflowMigrationPanel({ onMigrate, onClose, isLoading, mutation }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [explanation, setExplanation] = useState('');
  const [sourcePlatform, setSourcePlatform] = useState('');
  const fileInputRef = useRef(null);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a PNG, JPEG, GIF, or WebP image.');
      return;
    }

    // Validate file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      alert('Image is too large. Maximum size is 20MB.');
      return;
    }

    setSelectedImage(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleMigrate = async () => {
    if (!selectedImage || !explanation.trim()) return;

    try {
      const result = await mutation.mutateAsync({
        imageFile: selectedImage,
        explanation: explanation.trim(),
        sourcePlatform: sourcePlatform || null,
      });

      onMigrate(result);
    } catch (error) {
      console.error('Migration failed:', error);
    }
  };

  const platforms = [
    { value: '', label: 'Auto-detect' },
    { value: 'sumsub', label: 'Sumsub' },
    { value: 'persona', label: 'Persona' },
    { value: 'onfido', label: 'Onfido' },
    { value: 'jumio', label: 'Jumio' },
    { value: 'veriff', label: 'Veriff' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <div
      style={{
        padding: '16px',
        borderBottom: '1px solid var(--border-color)',
        background: 'linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%)',
        maxHeight: '70vh',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ArrowRightLeft size={16} style={{ color: 'var(--accent-primary)' }} />
          <span style={{ fontWeight: 600, fontSize: '14px' }}>Migrate Workflow</span>
        </div>
        <button onClick={onClose} style={styles.micButton}>
          <X size={16} />
        </button>
      </div>

      {/* Description */}
      <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: '1.5' }}>
        Upload a screenshot of your existing workflow and we'll generate an equivalent GetClearance workflow.
      </p>

      {/* Image Upload */}
      <div
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: imagePreview ? '2px solid var(--accent-primary)' : '2px dashed var(--border-color)',
          borderRadius: '8px',
          padding: imagePreview ? '0' : '20px',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s',
          background: imagePreview ? 'transparent' : 'var(--bg-primary)',
          marginBottom: '12px',
          overflow: 'hidden',
        }}
      >
        {imagePreview ? (
          <div style={{ position: 'relative' }}>
            <img
              src={imagePreview}
              alt="Selected screenshot"
              style={{ width: '100%', maxHeight: '150px', objectFit: 'contain' }}
            />
            <div
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: 'rgba(0,0,0,0.7)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '11px',
              }}
            >
              Click to change
            </div>
          </div>
        ) : (
          <>
            <Image size={24} style={{ color: 'var(--text-secondary)', marginBottom: '8px' }} />
            <div style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '4px' }}>
              Click to upload screenshot
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              PNG, JPEG, GIF, or WebP (max 20MB)
            </div>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          onChange={handleImageSelect}
          style={{ display: 'none' }}
        />
      </div>

      {/* Source Platform */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '4px', display: 'block' }}>
          Source Platform
        </label>
        <select
          value={sourcePlatform}
          onChange={(e) => setSourcePlatform(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            background: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            fontSize: '13px',
          }}
        >
          {platforms.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </div>

      {/* Explanation */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '4px', display: 'block' }}>
          What does this workflow do? <span style={{ color: 'var(--text-secondary)' }}>(required)</span>
        </label>
        <textarea
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
          placeholder="e.g., This is our KYC flow for crypto users. It verifies ID, takes a selfie with liveness, runs sanctions screening, and routes high-risk users to manual review..."
          style={{
            width: '100%',
            minHeight: '80px',
            padding: '10px 12px',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            background: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            fontSize: '13px',
            resize: 'vertical',
            lineHeight: '1.5',
          }}
        />
        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
          The more detail you provide, the more accurate the migration will be.
        </div>
      </div>

      {/* Error message */}
      {mutation.isError && (
        <div style={{
          padding: '8px 12px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '6px',
          marginBottom: '12px',
          fontSize: '12px',
          color: '#ef4444',
        }}>
          {mutation.error?.message || 'Migration failed. Please try again.'}
        </div>
      )}

      {/* Migrate Button */}
      <button
        onClick={handleMigrate}
        disabled={!selectedImage || !explanation.trim() || isLoading}
        style={{
          ...styles.sendButton,
          width: '100%',
          padding: '12px',
          fontSize: '14px',
          fontWeight: 600,
          borderRadius: '8px',
          opacity: (!selectedImage || !explanation.trim() || isLoading) ? 0.5 : 1,
        }}
      >
        {isLoading ? (
          <>
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
            Analyzing Screenshot...
          </>
        ) : (
          <>
            <ArrowRightLeft size={16} style={{ marginRight: '8px' }} />
            Migrate Workflow
          </>
        )}
      </button>
    </div>
  );
}

export default WorkflowChat;
